/*
 * Scroll reveal (replaces AOS)
 *
 * Elements that start below the fold fade and rise in as they reach the
 * viewport. Anything already on screen when this runs is left alone, and
 * nothing is hidden until this script has armed it, so no-JS visitors,
 * crawlers and a failed bundle always see the content.
 *
 *   data-reveal              rise + fade ("up", the default) or data-reveal="fade"
 *   data-reveal-delay="ms"   stagger, capped at 400ms
 *   data-reveal-anchor=".x"  reveal when that element arrives instead
 *   data-count="1234"        count up from 0 when revealed (data-dec="1" for decimals)
 *   data-reveal-ignore       on a container that runs its own reveal script
 *
 * Legacy AOS markup (data-aos="fade|fade-up", data-aos-delay, data-aos-anchor)
 * still works, with its delays halved. Templates no longer emit it: stale
 * critical CSS can still carry AOS's `[data-aos^=fade] { opacity: 0 }`.
 *
 * Styles: src/scss/utilities/motion.scss. Rules: .claude/docs/MOTION.md.
 */

const SELECTOR = "[data-reveal], [data-aos], [data-count]";
const DURATION = 600; // --dur-reveal
const MAX_DELAY = 400;
const COUNT_DURATION = 1100;

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const anchored = new Map(); // anchor element → elements it reveals
// The observed area runs from far above the viewport down to 8% above its
// bottom edge, so an element counts as arrived once it's anywhere above that
// line — including when End, a scrollbar drag or an anchor link jumps past it
// in one frame (a plain viewport observer never reports those).
const io = "IntersectionObserver" in window ? new IntersectionObserver(onIntersect, { rootMargin: "100000px 0px -8% 0px" }) : null;

function onIntersect(entries) {
	entries.forEach((entry) => {
		if (!entry.isIntersecting) return;
		const el = entry.target;
		io.unobserve(el);
		(anchored.get(el) || []).forEach(reveal);
		anchored.delete(el);
		reveal(el);
	});
}

function delayFor(el) {
	if (reduceMotion.matches) return 0;
	if (el.dataset.revealDelay != null) return Math.min(Number(el.dataset.revealDelay) || 0, MAX_DELAY);
	return Math.min((Number(el.dataset.aosDelay) || 0) / 2, MAX_DELAY);
}

function reveal(el) {
	if (el.classList.contains("is-reveal-pending")) {
		el.classList.remove("is-reveal-pending");
		// Drop the transition once it's done so the element's own (hover)
		// transitions apply again. A timer, because transitionend can bubble
		// up from children or never fire at all.
		setTimeout(() => el.classList.remove("is-reveal-armed"), DURATION + delayFor(el) + 50);
	}
	if (el.hasAttribute("data-count") && el.dataset.countFrom === "0") countUp(el);
}

function formatCount(value, decimals) {
	return value.toLocaleString("en-GB", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function countUp(el) {
	const target = parseFloat(el.dataset.count);
	const decimals = parseInt(el.dataset.dec || "0", 10);
	delete el.dataset.countFrom;
	if (isNaN(target)) return;
	if (reduceMotion.matches) {
		el.textContent = formatCount(target, decimals);
		return;
	}
	const start = performance.now();
	const step = (now) => {
		const p = Math.min(1, (now - start) / COUNT_DURATION);
		el.textContent = formatCount(target * (1 - Math.pow(1 - p, 3)), decimals);
		if (p < 1) requestAnimationFrame(step);
	};
	requestAnimationFrame(step);
}

export function scan(root = document) {
	if (!io || !root.querySelectorAll) return;

	const found = Array.from(root.querySelectorAll(SELECTOR));
	if (root.matches && root.matches(SELECTOR)) found.unshift(root);

	const fresh = found.filter((el) => !el.hasAttribute("data-reveal-bound") && !el.closest("[data-reveal-ignore], .swiper, .swiper-container"));
	if (!fresh.length) return;

	// Measure the whole batch before changing anything: one layout, not one per element.
	// Only arm elements comfortably below the fold: fonts, images and the Blitz
	// top bar still nudge the layout after this runs, and a hidden element that
	// drifts into view (then appears) counts as layout shift in Chrome.
	const fold = window.innerHeight * 1.25;
	const plans = fresh.map((el) => {
		const anchorSelector = el.dataset.revealAnchor || el.dataset.aosAnchor;
		const anchor = anchorSelector ? document.querySelector(anchorSelector) : null;
		const trigger = anchor || el;
		return { el, trigger, below: trigger.getBoundingClientRect().top >= fold };
	});

	plans.forEach(({ el, trigger, below }) => {
		el.setAttribute("data-reveal-bound", "");
		// Already on screen: leave it exactly as rendered.
		if (!below) return;

		if (el.hasAttribute("data-count")) {
			el.dataset.countFrom = "0";
			el.textContent = formatCount(0, parseInt(el.dataset.dec || "0", 10));
		}
		if (el.hasAttribute("data-reveal") || el.hasAttribute("data-aos")) {
			el.style.setProperty("--reveal-delay", `${delayFor(el)}ms`);
			el.classList.add("is-reveal-armed", "is-reveal-pending");
		}

		if (trigger === el) {
			io.observe(el);
		} else {
			if (!anchored.has(trigger)) anchored.set(trigger, []);
			anchored.get(trigger).push(el);
			io.observe(trigger);
		}
	});
}

scan();

// Sprig/htmx and Blitz dynamic includes add markup after load.
document.addEventListener("htmx:afterSettle", (event) => scan(event.target));
document.addEventListener("afterBlitzInjectAll", () => scan());
